import { buildAppLoginRedirectPath, getAppAuthRedirectFromRoute } from '~/utils/appAuthRedirect'

/**
 * Admin session for the /app/2 "Atelier" backoffice: verifies the admin role
 * once, exposes bearer headers for /api/app2/* calls.
 */
const state = {
  email: ref(''),
  status: ref<'checking' | 'authorized' | 'denied'>('checking')
}

export function useAtelierSession() {
  const supabase = useSupabaseClient()
  const route = useRoute()

  const loginPath = (error?: string) => buildAppLoginRedirectPath(
    getAppAuthRedirectFromRoute(route),
    error ? { error } : {}
  )

  const verify = async () => {
    if (state.status.value === 'authorized') return true

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) {
        state.status.value = 'denied'
        await navigateTo(loginPath())
        return false
      }

      const response = await $fetch<{ authorized?: boolean }>('/api/auth-check', {
        method: 'POST',
        headers: await getBearerHeaders(),
        body: { email: user.email, requiredRole: 'admin' }
      })

      if (!response?.authorized) {
        state.status.value = 'denied'
        await supabase.auth.signOut()
        await navigateTo(loginPath('unauthorized'))
        return false
      }

      state.email.value = user.email
      state.status.value = 'authorized'
      return true
    } catch (error) {
      console.error('[Atelier] authorization check failed:', error)
      state.status.value = 'denied'
      await navigateTo(loginPath('auth_check_failed'))
      return false
    }
  }

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) throw new Error('Session admin introuvable')
    return { Authorization: `Bearer ${session.access_token}` }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    state.status.value = 'checking'
    state.email.value = ''
    await navigateTo('/app/login')
  }

  return {
    email: state.email,
    status: state.status,
    verify,
    authHeaders,
    signOut
  }
}
